import type { JsonSchemaType } from '@gmn-portal/backend';
import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useIntl } from '@/core/i18n/use-intl';
import { Dialog, DialogContent, Toggle } from '@/shared/components/ui';
import { cn } from '@/shared/utils';
import type { MetadataBuilderHeader } from './index';
import { MetadataBuilder } from './index';
import { MetadataFormPreview } from './metadata-form-preview';
import type { MetadataBuilderProps } from './types';

type PreviewStorageMode = 'remember' | 'session';

type MetadataBuilderWithPreviewProps = Pick<
  MetadataBuilderProps,
  'value' | 'onChange' | 'disabled' | 'userGroups' | 'defaultUpdateGroupKeys' | 'fieldErrors'
> & {
  className?: string;
  storageKey?: string;
  previewDefaultOpen?: boolean;
  previewStorage?: PreviewStorageMode;
  header?: MetadataBuilderHeader | false;
};

const DEFAULT_STORAGE_KEY = 'gmn.metadataBuilder.formPreview.open';

function readStoredBool(storageKey: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === null) return fallback;
    return raw === '1';
  } catch {
    return fallback;
  }
}

function writeStoredBool(storageKey: string, value: boolean) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, value ? '1' : '0');
  } catch {
    // ignore
  }
}

export function MetadataBuilderWithPreview({
  value,
  onChange,
  disabled,
  userGroups,
  defaultUpdateGroupKeys,
  fieldErrors,
  className,
  storageKey,
  previewDefaultOpen = false,
  previewStorage = 'remember',
  header = false,
}: MetadataBuilderWithPreviewProps) {
  const { t } = useIntl();

  const resolvedStorageKey = storageKey ?? DEFAULT_STORAGE_KEY;

  const [isPreviewOpen, setIsPreviewOpen] = useState(() => {
    if (previewStorage === 'session') return previewDefaultOpen;
    return readStoredBool(resolvedStorageKey, previewDefaultOpen);
  });

  useEffect(() => {
    if (previewStorage === 'session') return;
    writeStoredBool(resolvedStorageKey, isPreviewOpen);
  }, [isPreviewOpen, previewStorage, resolvedStorageKey]);

  return (
    <div className={cn('flex flex-col h-full', className)}>
      <MetadataBuilder
        value={value}
        onChange={onChange}
        disabled={disabled}
        userGroups={userGroups}
        defaultUpdateGroupKeys={defaultUpdateGroupKeys}
        fieldErrors={fieldErrors}
        className="border-0 rounded-none shadow-none h-full"
        header={header}
        tabsHeaderRight={
          <Toggle
            size="sm"
            variant="outline"
            pressed={isPreviewOpen}
            onPressedChange={setIsPreviewOpen}
            data-testid="metadata-form-preview-toggle"
            aria-label={t('metadata.builder.preview')}
            className="text-muted-foreground h-7 text-xs mb-0.5"
          >
            {isPreviewOpen ? <EyeOff /> : <Eye />}
            <span>{t('metadata.builder.preview')}</span>
          </Toggle>
        }
      />

      <Dialog open={isPreviewOpen} onOpenChange={(next) => !next && setIsPreviewOpen(false)}>
        <DialogContent className="max-w-[98vw]! w-[1400px]! max-h-[90vh] overflow-hidden p-0">
          <div className="h-[90vh] overflow-hidden">
            <MetadataFormPreview
              schema={value as JsonSchemaType | undefined}
              className="border-0 rounded-none shadow-none h-full"
              header={false}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
